import { clamp } from "../physics/numbers";
import { createRivalInput, createRivalProfiles, sampleTrackCurvature } from "./ai";
import { createVehicleDynamics, resolveGameConfig } from "./config";
import { resolveVehicleContacts } from "./contact";
import { createNeutralInput, sanitizeInputFrame } from "./input";
import {
  advanceRaceProgress,
  createRaceProgressState,
  getRaceDistance,
  getPlayerPosition
} from "./race";
import type {
  GameConfig,
  GameState,
  InputFrame,
  RaceProgressState,
  RaceRecord,
  RivalProfile,
  SimulationUpdate,
  VehicleDynamics,
  VehicleState
} from "./types";
import {
  cloneVehicleState,
  createVehicleState,
  recoverVehicle,
  stepVehicle
} from "./vehicle";

const PLAYER_ID = "player";
const MAXIMUM_FRAME_DELTA = 0.25;
const FINISH_COAST_DURATION = 2.4;

const cloneGameState = (state: GameState): GameState => ({
  ...state,
  finishOrder: [...state.finishOrder],
  player: cloneVehicleState(state.player),
  rivals: state.rivals.map(cloneVehicleState)
});

const createInitialVehicles = (
  profiles: readonly RivalProfile[],
  trackLength: number
): { player: VehicleState; rivals: VehicleState[] } => {
  const gridSpacing = 5.8 / trackLength;
  const playerProgress = 0.018;

  return {
    player: createVehicleState(PLAYER_ID, playerProgress, 0),
    rivals: profiles.map((profile, index) =>
      createVehicleState(
        profile.id,
        playerProgress + (index + 1) * gridSpacing,
        (index % 2 === 0 ? -1 : 1) * (1.85 + Math.floor(index / 2) * 0.34)
      )
    )
  };
};

export class SuntraceSimulation {
  readonly config: GameConfig;
  readonly fixedStep: number;
  state: GameState;
  previousState: GameState;
  interpolationAlpha = 0;
  private accumulator = 0;
  private pendingRecovery = false;
  private readonly playerDynamics: VehicleDynamics;
  private readonly rivalProfiles: RivalProfile[];
  private progressById = new Map<string, RaceProgressState>();
  private readonly finishCoastRemainingById = new Map<string, number>();

  constructor(configOverrides: Partial<GameConfig> = {}) {
    this.config = resolveGameConfig(configOverrides);
    this.fixedStep = this.config.fixedStep;
    this.playerDynamics = createVehicleDynamics(this.config);
    this.rivalProfiles = createRivalProfiles(this.config);
    this.state = this.createInitialState(this.config.initialBestLapTime);
    this.previousState = cloneGameState(this.state);
  }

  update(deltaSeconds: number, input: InputFrame): SimulationUpdate {
    const frame = sanitizeInputFrame(input);

    if (frame.resetPressed) {
      this.reset();
      return { steps: 0, interpolationAlpha: this.interpolationAlpha };
    }

    if (frame.pausePressed) {
      this.setPaused(!this.state.paused);
    }

    if (
      this.state.paused ||
      this.state.phase === "finished" ||
      !Number.isFinite(deltaSeconds) ||
      deltaSeconds <= 0
    ) {
      return { steps: 0, interpolationAlpha: this.interpolationAlpha };
    }

    this.pendingRecovery ||= frame.recoverPressed;
    this.accumulator += Math.min(deltaSeconds, MAXIMUM_FRAME_DELTA);
    let steps = 0;

    while (this.accumulator >= this.fixedStep) {
      const fixedInput = createNeutralInput({
        ...frame,
        recoverPressed: this.pendingRecovery,
        pausePressed: false,
        resetPressed: false
      });
      this.pendingRecovery = false;
      this.previousState = cloneGameState(this.state);
      this.advanceFixed(fixedInput);
      this.accumulator = Math.max(0, this.accumulator - this.fixedStep);
      steps += 1;

      if (this.state.finishTime !== null) {
        this.accumulator = 0;
        break;
      }
    }

    this.interpolationAlpha = this.accumulator / this.fixedStep;
    return { steps, interpolationAlpha: this.interpolationAlpha };
  }

  stepFixed(input: InputFrame = createNeutralInput()): void {
    const frame = sanitizeInputFrame(input);

    if (frame.resetPressed) {
      this.reset();
      return;
    }

    if (frame.pausePressed) {
      this.setPaused(!this.state.paused);
    }

    if (this.state.paused || this.state.phase === "finished") {
      return;
    }

    this.previousState = cloneGameState(this.state);
    this.advanceFixed(frame);
    this.interpolationAlpha = 0;
  }

  setPaused(paused: boolean): void {
    this.state.paused = paused;
    this.accumulator = 0;
    this.interpolationAlpha = 0;
    this.previousState = cloneGameState(this.state);
  }

  reset(preserveBestLap = true): void {
    const bestLapTime = preserveBestLap
      ? this.state.bestLapTime
      : this.config.initialBestLapTime;
    this.accumulator = 0;
    this.pendingRecovery = false;
    this.interpolationAlpha = 0;
    this.state = this.createInitialState(bestLapTime);
    this.previousState = cloneGameState(this.state);
  }

  recoverPlayer(): boolean {
    if (this.state.paused || this.state.phase !== "racing") {
      return false;
    }

    this.previousState = cloneGameState(this.state);
    this.recoverTrackedVehicle(this.state.player);
    return true;
  }

  getRaceProgress(vehicleId: string): RaceProgressState | null {
    const progress = this.progressById.get(vehicleId);
    return progress === undefined ? null : { ...progress };
  }

  isVehicleVisible(vehicleId: string): boolean {
    const progress = this.progressById.get(vehicleId);
    return progress?.finished !== true || (this.finishCoastRemainingById.get(vehicleId) ?? 0) > 0;
  }

  getRaceRecord(): RaceRecord {
    return { version: 1, bestLapTime: this.state.bestLapTime };
  }

  applyRaceRecord(record: RaceRecord): void {
    const bestLapTime =
      record.bestLapTime === null ||
      (Number.isFinite(record.bestLapTime) && record.bestLapTime > 0)
        ? record.bestLapTime
        : null;
    const playerProgress = this.progressById.get(PLAYER_ID);

    if (playerProgress !== undefined) {
      playerProgress.bestLapTime = bestLapTime;
    }

    this.state.bestLapTime = bestLapTime;
    this.previousState.bestLapTime = bestLapTime;
  }

  private createInitialState(bestLapTime: number | null): GameState {
    const vehicles = createInitialVehicles(this.rivalProfiles, this.config.trackLength);
    this.progressById = new Map<string, RaceProgressState>();
    this.finishCoastRemainingById.clear();
    this.progressById.set(
      vehicles.player.id,
      createRaceProgressState(vehicles.player.trackProgress, bestLapTime)
    );

    for (const rival of vehicles.rivals) {
      this.progressById.set(rival.id, createRaceProgressState(rival.trackProgress));
    }

    const position = getPlayerPosition(
      vehicles.player,
      vehicles.rivals,
      this.progressById,
      this.config.totalLaps
    );

    return {
      phase: this.config.countdownDuration > 0 ? "countdown" : "racing",
      paused: false,
      countdownRemaining: this.config.countdownDuration,
      raceTime: 0,
      totalLaps: this.config.totalLaps,
      lap: 1,
      position,
      currentLapTime: 0,
      lastLapTime: null,
      bestLapTime,
      finishTime: null,
      finishOrder: [],
      player: vehicles.player,
      rivals: vehicles.rivals
    };
  }

  private advanceFixed(input: InputFrame): void {
    if (this.state.phase === "countdown") {
      this.state.countdownRemaining = Math.max(
        0,
        this.state.countdownRemaining - this.fixedStep
      );

      if (this.state.countdownRemaining === 0) {
        this.state.phase = "racing";
      }

      return;
    }

    const playerRecovered = input.recoverPressed;

    if (playerRecovered) {
      this.recoverTrackedVehicle(this.state.player);
    }

    const trafficSnapshot = [
      cloneVehicleState(this.state.player),
      ...this.state.rivals.map(cloneVehicleState)
    ];
    if (!playerRecovered) {
      stepVehicle(
        this.state.player,
        input,
        this.playerDynamics,
        this.fixedStep,
        sampleTrackCurvature(this.state.player.trackProgress)
      );
    }

    for (let index = 0; index < this.state.rivals.length; index += 1) {
      const rival = this.state.rivals[index];
      const profile = this.rivalProfiles[index];

      if (rival === undefined || profile === undefined) {
        continue;
      }

      const rivalProgress = this.progressById.get(rival.id);

      if (rivalProgress?.finished === true) {
        const remaining = this.finishCoastRemainingById.get(rival.id) ?? 0;
        if (remaining > 0) {
          stepVehicle(
            rival,
            createNeutralInput({ brake: 0.18 }),
            profile.dynamics,
            this.fixedStep,
            sampleTrackCurvature(rival.trackProgress)
          );
          this.finishCoastRemainingById.set(
            rival.id,
            Math.max(0, remaining - this.fixedStep)
          );
        } else {
          rival.speed = 0;
          rival.acceleration = 0;
          rival.lateralVelocity = 0;
          rival.feedback.boost = 0;
          rival.feedback.impact = 0;
        }
        continue;
      }

      const aiInput = createRivalInput(rival, profile, trafficSnapshot);
      stepVehicle(
        rival,
        aiInput,
        profile.dynamics,
        this.fixedStep,
        sampleTrackCurvature(rival.trackProgress)
      );

      if (
        Math.abs(rival.lateralOffset) >= profile.dynamics.roadHalfWidth * 0.995 &&
        rival.speed < 11 &&
        rival.impactCooldown > 0
      ) {
        this.recoverTrackedVehicle(rival);
      }
    }

    const activeVehicles = [this.state.player, ...this.state.rivals].filter((vehicle) =>
      this.isVehicleVisible(vehicle.id)
    );
    resolveVehicleContacts(
      activeVehicles,
      this.config.trackLength,
      this.config.roadHalfWidth
    );
    this.advanceRaceTrackers();
    this.syncRaceState();
  }

  private advanceRaceTrackers(): void {
    const vehicles = [this.state.player, ...this.state.rivals];

    for (const vehicle of vehicles) {
      const progress = this.progressById.get(vehicle.id);

      if (progress === undefined || progress.finished) {
        continue;
      }

      const event = advanceRaceProgress(
        progress,
        vehicle.trackProgress,
        this.fixedStep,
        this.config.totalLaps
      );

      if (event.finished && !this.state.finishOrder.includes(vehicle.id)) {
        this.state.finishOrder.push(vehicle.id);
        this.finishCoastRemainingById.set(vehicle.id, FINISH_COAST_DURATION);
      }
    }
  }

  private syncRaceState(): void {
    const playerProgress = this.progressById.get(PLAYER_ID);

    if (playerProgress === undefined) {
      return;
    }

    this.state.raceTime = playerProgress.raceTime;
    this.state.lap = playerProgress.lap;
    this.state.currentLapTime = playerProgress.currentLapTime;
    this.state.lastLapTime = playerProgress.lastLapTime;
    this.state.bestLapTime = playerProgress.bestLapTime;
    this.state.finishTime = playerProgress.finishTime;
    this.state.position = getPlayerPosition(
      this.state.player,
      this.state.rivals,
      this.progressById,
      this.config.totalLaps
    );

    if (playerProgress.finished) {
      this.state.phase = "finished";
      this.state.paused = false;
      this.finalizeFinishOrder();
      this.previousState = cloneGameState(this.state);
    }
  }

  private finalizeFinishOrder(): void {
    const classifiedIds = new Set(this.state.finishOrder);
    const remainingVehicles = [this.state.player, ...this.state.rivals]
      .filter((vehicle) => !classifiedIds.has(vehicle.id))
      .sort((first, second) => {
        const firstProgress = this.progressById.get(first.id);
        const secondProgress = this.progressById.get(second.id);

        if (firstProgress === undefined || secondProgress === undefined) {
          return first.id.localeCompare(second.id);
        }

        const distanceDifference =
          getRaceDistance(secondProgress, second, this.config.totalLaps) -
          getRaceDistance(firstProgress, first, this.config.totalLaps);
        return distanceDifference === 0 ? first.id.localeCompare(second.id) : distanceDifference;
      });

    this.state.finishOrder.push(...remainingVehicles.map((vehicle) => vehicle.id));
  }

  private recoverTrackedVehicle(vehicle: VehicleState): void {
    recoverVehicle(vehicle);
    const progress = this.progressById.get(vehicle.id);

    if (progress !== undefined && !progress.finished) {
      progress.lastProgress = vehicle.trackProgress;
    }
  }
}

export const createGameSimulation = (
  config: Partial<GameConfig> = {}
): SuntraceSimulation => new SuntraceSimulation(config);

export const updateGameSimulation = (
  simulation: SuntraceSimulation,
  input: InputFrame,
  deltaSeconds: number
): SimulationUpdate => simulation.update(deltaSeconds, input);

export const resetGameSimulation = (
  simulation: SuntraceSimulation,
  preserveBestLap = true
): void => simulation.reset(preserveBestLap);

export const setGamePaused = (simulation: SuntraceSimulation, paused: boolean): void => {
  simulation.setPaused(paused);
};

export const recoverPlayer = (simulation: SuntraceSimulation): boolean =>
  simulation.recoverPlayer();

export const clampInterpolationAlpha = (value: number): number => clamp(value, 0, 1);
